/**
 * 콘텐츠의 [화면 편집] — 무엇을 고치고 어디에 저장하나(2026-10-30). 편집 화면 자체는 `HtmlVisualEditor`.
 *
 * - 올린 HTML: 그 문서를 고쳐 **같은 콘텐츠**에 저장한다. 쓰던 편성은 그대로 새 내용을 튼다(서버가 주소의 판을 바꿔
 *   화면이 새로 받는다). 화면 크기에 맞춰 늘고 주는 문서는 고른 편집 크기로 굳힌다 — 옮긴 자리가 화면마다 어긋나지 않게.
 * - 템플릿: 그 순간의 모습을 HTML 사본으로 옮겨 고치고 **새 HTML 콘텐츠**로 저장한다(`templateHtml.ts`). 원래 템플릿은
 *   남는다. 고르면 그 템플릿을 쓰던 플레이리스트의 줄을 새 콘텐츠로 바꿔 넣는다.
 * - 빈 화면(콘텐츠 ▸ [콘텐츠 추가] ▸ [빈 화면에서 만들기]): 흰 바탕의 빈 문서(`blankDocument`)를 [넣기] 를 펼친 채로 열고,
 *   저장하면 **새 HTML 콘텐츠**가 된다. 편집 크기가 기준 크기다.
 * - 등록한 템플릿(콘텐츠 ▸ [템플릿 만들기] ▸ 등록한 템플릿, 2026-10-30): 이름을 정하면 그 HTML 의 사본을 열고, 저장하면
 *   **새 HTML 콘텐츠**가 된다(원본은 그대로). 기준 크기·맞춤은 원본을 따른다.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatePayloadSchema, zoneSlideIndex, type TemplateMedia, type TemplatePayload } from '@signageone/core';
import type { Asset, SignageOneClient } from '../api/client.js';
import { useBrand } from '../components/brand.js';
import { templateFontUrl } from '../components/templateFonts.js';
import { Loading, ErrorNote, Modal } from '../components/ui.js';
import { HtmlVisualEditor, type DesignSize, type HtmlVisualEditorProps } from './HtmlVisualEditor.js';
import {
  TEMPLATE_HTML_SIZE,
  imageDataUrl,
  templateFontFiles,
  templateHtmlDocument,
  templateVideoRef,
  toDataUrl,
} from './templateHtml.js';
import { blankDocument, withDocumentTitle } from './components.js';
import { htmlDesign } from '../components/HtmlContent.js';

interface Props {
  client: SignageOneClient;
  asset: Asset;
  /** 플레이리스트를 고칠 수 있나(`schedule:write`) — 템플릿 사본을 바꿔 넣을지 묻는다. */
  canReplace: boolean;
  onClose: () => void;
  /** 올린 HTML 을 저장했을 때 — 템플릿 사본은 새 콘텐츠로 옮겨 가므로 부르지 않는다. */
  onSaved: () => void;
}

/**
 * [넣기] ▸ [영상](2026-10-30) — 고른 영상을 콘텐츠로 올리고(서버가 화면이 푸는 모양으로 바꾼다, 같은 파일이면 있던 것을 쓴다),
 * 문서의 영상은 편집 화면에서 콘솔의 미리보기 주소로 보인다. 태그는 그 화면을 만드는 콘텐츠와 같게.
 */
function videoProps(
  client: SignageOneClient,
  tags: readonly string[],
): Pick<HtmlVisualEditorProps, 'uploadVideo' | 'videoSource'> {
  return {
    uploadVideo: async (file) => ({ id: (await client.uploadAsset(file, tags)).id }),
    videoSource: async (id) => (await client.getAsset(id)).preview_url ?? null,
  };
}

/** 늘고 주는 HTML 을 고칠 크기 — 콘솔의 미리보기 기본과 같다(`HtmlContent.tsx`). */
const SIZE_CHOICES: ReadonlyArray<{ label: string } & DesignSize> = [
  { label: '가로 화면', width: 1920, height: 1080 },
  { label: '세로 화면', width: 1080, height: 1920 },
];

export function ScreenEditor(props: Props): ReactNode {
  return props.asset.type === 'template' ? <TemplateScreenEditor {...props} /> : <HtmlScreenEditor {...props} />;
}

function HtmlScreenEditor({ client, asset, onClose, onSaved }: Props): ReactNode {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    client.assetHtml(asset.id).then(
      (found) => live && setHtml(found.html),
      (cause: unknown) => live && setError(cause instanceof Error ? cause.message : 'HTML 을 읽지 못했습니다'),
    );
    return () => {
      live = false;
    };
  }, [client, asset.id]);

  const fixed = asset.width && asset.height ? { width: asset.width, height: asset.height } : null;
  if (html === null) return <Preparing error={error} onClose={onClose} />;
  const notes = [
    fixed
      ? null
      : '이 문서는 화면 크기에 맞춰 늘고 주는 HTML 입니다. 저장하면 고른 편집 크기로 굳혀, 화면마다 그 크기를 통째로 맞춰 띄웁니다.',
    // 우리가 끼운 것(앞머리 스크립트·값·D-day)은 빼고 본다 — 서버가 받은 HTML 에는 늘 앞머리 스크립트가 있다.
    /<script(?![^>]*\bdata-so-(?:compat|values|dday-script|video-script)\b)[\s>]/i.test(html)
      ? '이 문서의 스크립트는 편집 화면에서 돌지 않습니다 — 스크립트가 그리는 부분은 비어 보일 수 있지만 화면에서는 그대로 돕니다.'
      : null,
  ].filter(Boolean);
  return (
    <HtmlVisualEditor
      title={asset.title}
      html={html}
      design={fixed ?? SIZE_CHOICES[0]!}
      sizeChoices={fixed ? undefined : SIZE_CHOICES}
      note={notes.length > 0 ? notes.join(' ') : undefined}
      {...videoProps(client, asset.tags)}
      onCancel={onClose}
      onSave={async (edited, design) => {
        await client.saveAssetHtml(asset.id, edited, fixed ? undefined : design);
        onSaved();
      }}
    />
  );
}

function TemplateScreenEditor({ client, asset, canReplace, onClose }: Props): ReactNode {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const usedIn = (asset.used_in ?? []).filter((usage) => usage.kind !== 'schedule');
  const [replace, setReplace] = useState(canReplace && usedIn.length > 0);

  // 사본을 만든다 — 글꼴·로고를 받아 `data:` 로 품어야 해서 기다린다(글꼴 한 벌 수백 KB).
  useEffect(() => {
    let live = true;
    const parsed = templatePayloadSchema.safeParse(asset.template);
    if (!parsed.success) {
      setError('이 템플릿의 내용이 형식과 맞지 않아 옮길 수 없습니다');
      return;
    }
    const payload = parsed.data;
    const now = new Date();
    void (async () => {
      const files = templateFontFiles(payload);
      const [fonts, logo, font, media] = await Promise.all([
        Promise.all(files.map(async (file) => [file, await toDataUrl(templateFontUrl(file))] as const)),
        toDataUrl(brand.logoUrl),
        toDataUrl(brand.font?.url),
        templateMedia(client, payload, now),
      ]);
      if (!live) return;
      setHtml(
        templateHtmlDocument({
          payload,
          now,
          // 콘솔의 템플릿 미리보기와 같은 시간대(`TemplatePreview.tsx`).
          timezone: 'Asia/Seoul',
          location: '',
          brand: {
            name: brand.name,
            logoDataUrl: logo,
            accent: brand.accent,
            font: font && brand.font ? { dataUrl: font, mime: brand.font.mime } : null,
          },
          fontData: Object.fromEntries(fonts.filter((entry): entry is readonly [string, string] => entry[1] !== null)),
          media,
        }),
      );
    })();
    return () => {
      live = false;
    };
  }, [client, asset.template, brand]);

  if (html === null) return <Preparing error={error} onClose={onClose} />;
  return (
    <HtmlVisualEditor
      title={asset.title}
      html={html}
      design={TEMPLATE_HTML_SIZE}
      note={
        <>
          템플릿의 지금 모습을 <b>HTML 사본</b>으로 옮겨 고칩니다. 저장하면 새 HTML 콘텐츠 「{asset.title} (편집)」 이
          생기고 원래 템플릿은 그대로 남습니다. 사본에서는 시계·남은 시간만 저절로 바뀌고, 글은 [내용 편집] 이 아니라 이
          화면에서 고칩니다.
        </>
      }
      saveLabel="새 콘텐츠로 저장"
      saveExtras={
        canReplace && usedIn.length > 0 ? (
          <label
            className="mr-1 flex items-center gap-1.5 text-[12.5px] text-slate-600"
            title={usedIn.map((usage) => usage.name).join(', ')}
          >
            <input type="checkbox" checked={replace} onChange={(event) => setReplace(event.target.checked)} />
            플레이리스트 {usedIn.length}개에서 바꿔 넣기
          </label>
        ) : null
      }
      {...videoProps(client, asset.tags)}
      onCancel={onClose}
      onSave={async (edited) => {
        const title = `${asset.title} (편집)`;
        const file = new File([edited], `${title}.html`, { type: 'text/html' });
        const created = await client.uploadAsset(file, asset.tags, title);
        // 사본은 1920×1080 에서 자리를 정했다 — 화면마다 그 크기를 통째로 맞춘다.
        await client.updateAsset(created.id, {
          html_size: { width: TEMPLATE_HTML_SIZE.width, height: TEMPLATE_HTML_SIZE.height },
          html_size_manual: true,
          html_fit: 'auto',
        });
        if (replace) await client.replaceInPlaylists(asset.id, created.id);
        // 닫고 옮긴다 — 같은 상세 화면이 id 만 바뀌어 다시 그려지므로, 닫지 않으면 새 콘텐츠의 편집 창이 이어서 뜬다.
        onClose();
        navigate(`/content/${created.id}`);
      }}
    />
  );
}

/**
 * 템플릿의 그림·영상을 사본에 — 이미지는 줄여서 품고(`data:`), 영상은 콘텐츠를 가리킨다([화면 편집] 의 영상과 같은 약속).
 * 고르지 않았으면 빈 것. HTML 칸(2026-11-03)은 옮기지 않는다 — 사본은 한 장짜리 HTML 이라 남의 HTML 을 품을 자리가 없다(그
 * 자리는 장식 무늬가 된다).
 *
 * 구역 나누기(2026-11-03)는 구역마다 **그 순간 보이는 장 하나**만 — 사본은 장을 넘기지 않는다(그림 열 장을 다 품으면 올린
 * HTML 상한을 넘기기도 한다).
 */
async function templateMedia(client: SignageOneClient, payload: TemplatePayload, now: Date): Promise<Record<string, string>> {
  const chosen: TemplateMedia[] =
    payload.id === 'split'
      ? payload.data.media
        ? [payload.data.media]
        : []
      : payload.id === 'zones'
        ? payload.data.zones.flatMap((zone) =>
            zone.kind === 'media' && zone.items.length > 0
              ? [zone.items[zoneSlideIndex(now.getTime(), zone.dwellSec, zone.items.length)] as TemplateMedia]
              : [],
          )
        : [];
  const entries = await Promise.all(
    chosen.map(async ({ assetId, kind }): Promise<Array<[string, string]>> => {
      if (kind === 'html') return [];
      if (kind === 'video') return [[assetId, templateVideoRef(assetId)]];
      const found = await client.getAsset(assetId).catch(() => null);
      const data = await imageDataUrl(found?.preview_url);
      return data ? [[assetId, data]] : [];
    }),
  );
  return Object.fromEntries(entries.flat());
}

/** 새로 만들 화면의 방향 — 늘고 주는 HTML 의 편집 크기와 같다. */
const NEW_SIZES = SIZE_CHOICES;

/**
 * [빈 화면에서 만들기] — 이름과 방향을 고르면 빈 화면으로 편집기를 연다. 저장하면 새 HTML 콘텐츠를 올리고 그 크기를
 * 기준 크기로 적은 뒤(화면마다 통째로 맞춤) 그 콘텐츠의 상세로 간다.
 */
export function NewScreenEditor({
  client,
  tags = [],
  onClose,
}: {
  client: SignageOneClient;
  /** [콘텐츠 추가] 의 업로드 옵션에 적어 둔 태그. */
  tags?: string[];
  onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const [start, setStart] = useState<{ title: string; design: DesignSize } | null>(null);
  const html = useMemo(() => (start ? blankDocument(start.title) : ''), [start]);
  if (!start) return <NewScreenDialog onClose={onClose} onStart={setStart} />;
  return (
    <HtmlVisualEditor
      title={start.title}
      html={html}
      design={start.design}
      sizeChoices={NEW_SIZES}
      startWithInsert
      saveLabel="새 콘텐츠로 저장"
      {...videoProps(client, tags)}
      onCancel={onClose}
      onSave={async (edited, design) => {
        const file = new File([edited], `${start.title}.html`, { type: 'text/html' });
        const created = await client.uploadAsset(file, tags, start.title);
        await client.updateAsset(created.id, {
          html_size: { width: design.width, height: design.height },
          html_size_manual: true,
          html_fit: 'auto',
        });
        onClose();
        navigate(`/content/${created.id}`);
      }}
    />
  );
}

/**
 * 등록한 템플릿(올린 HTML)에서 새로 만들기 — 이름을 정하면 그 문서의 사본을 [화면 편집] 으로 연다. 저장하면 새 HTML 콘텐츠를
 * 올리고 그 상세로 간다. 태그는 원본의 것에 [콘텐츠 추가] 의 업로드 옵션 태그를 더한다.
 */
export function TemplateCopyEditor({
  client,
  source,
  tags = [],
  onClose,
}: {
  client: SignageOneClient;
  source: Asset;
  tags?: string[];
  onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const templateName = source.template_entry?.name ?? source.title;
  const [title, setTitle] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    client.assetHtml(source.id).then(
      (found) => live && setHtml(found.html),
      (cause: unknown) => live && setError(cause instanceof Error ? cause.message : 'HTML 을 읽지 못했습니다'),
    );
    return () => {
      live = false;
    };
  }, [client, source.id]);
  const copy = useMemo(() => (html !== null && title !== null ? withDocumentTitle(html, title) : null), [html, title]);
  const allTags = useMemo(() => [...new Set([...source.tags, ...tags])], [source.tags, tags]);

  if (title === null) {
    return <CopyNameDialog templateName={templateName} onClose={onClose} onStart={setTitle} />;
  }
  if (copy === null) return <Preparing error={error} onClose={onClose} />;
  const fixed = htmlDesign(source);
  return (
    <HtmlVisualEditor
      title={title}
      html={copy}
      design={fixed ?? SIZE_CHOICES[0]!}
      sizeChoices={fixed ? undefined : SIZE_CHOICES}
      note={
        <>
          등록한 템플릿 「{templateName}」 의 <b>사본</b>입니다. 저장하면 새 콘텐츠 「{title}」 이 생기고 템플릿은 그대로
          남습니다.
        </>
      }
      saveLabel="새 콘텐츠로 저장"
      savePristine
      {...videoProps(client, allTags)}
      onCancel={onClose}
      onSave={async (edited, design) => {
        const file = new File([edited], `${title}.html`, { type: 'text/html' });
        const created = (await client.uploadAsset(file, allTags, title)) as Asset & { deduplicated?: boolean };
        // 같은 글자는 한 콘텐츠다 — 서버가 있던 것을 돌려준다. 그것의 크기를 고치지 않게 여기서 멈춘다.
        if (created.deduplicated) {
          throw new Error(`같은 내용의 콘텐츠 「${created.title}」 가 이미 있습니다 — 이름이나 내용을 바꾼 뒤 저장하세요`);
        }
        await client.updateAsset(created.id, {
          html_size: { width: design.width, height: design.height },
          html_size_manual: true,
          html_fit: source.html_fit ?? 'auto',
        });
        onClose();
        navigate(`/content/${created.id}`);
      }}
    />
  );
}

function CopyNameDialog({
  templateName,
  onClose,
  onStart,
}: {
  templateName: string;
  onClose: () => void;
  onStart: (title: string) => void;
}): ReactNode {
  const [title, setTitle] = useState(templateName);
  return (
    <Modal title={`「${templateName}」 에서 새로 만들기`} onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          onStart(title.trim() || templateName);
        }}
      >
        <p className="text-[13px] leading-relaxed text-slate-600">
          등록한 템플릿의 사본을 화면 편집으로 엽니다. 바꿀 글·그림·날짜를 고쳐 저장하면 새 콘텐츠가 되고, 템플릿은 그대로
          남습니다.
        </p>
        <label className="field">
          <span>새 콘텐츠 이름</span>
          <input
            className="input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoFocus
            required
          />
        </label>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary">
            열기
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NewScreenDialog({
  onClose,
  onStart,
}: {
  onClose: () => void;
  onStart: (start: { title: string; design: DesignSize }) => void;
}): ReactNode {
  const [title, setTitle] = useState('새 화면');
  const [size, setSize] = useState(0);
  return (
    <Modal title="빈 화면에서 만들기" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const choice = NEW_SIZES[size]!;
          onStart({ title: title.trim() || '새 화면', design: { width: choice.width, height: choice.height } });
        }}
      >
        <p className="text-[13px] leading-relaxed text-slate-600">
          흰 화면에 제목·글·그림·D-day·입력 칸 등을 넣어 새 HTML 콘텐츠를 만듭니다. 화면 편집과 같은 편집기가 열립니다.
        </p>
        <label className="field">
          <span>이름</span>
          <input
            className="input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoFocus
            required
          />
        </label>
        <div className="field">
          <span>화면 방향</span>
          <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup" aria-label="화면 방향">
            {NEW_SIZES.map((choice, index) => (
              <button
                key={choice.label}
                type="button"
                role="radio"
                aria-checked={size === index}
                onClick={() => setSize(index)}
                className={`flex flex-col items-center gap-2 rounded-xl border px-3 py-3 text-[13px] ${
                  size === index
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="flex h-12 items-center justify-center">
                  <span
                    className={`rounded-[3px] border-2 ${size === index ? 'border-blue-500 bg-white' : 'border-slate-300 bg-white'}`}
                    style={choice.width > choice.height ? { width: 64, height: 36 } : { width: 27, height: 48 }}
                  />
                </span>
                <span className="font-medium">{choice.label}</span>
                <span className="text-[12px] text-slate-500">
                  {choice.width} × {choice.height}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary">
            만들기
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** 문서를 받는 동안 — 편집 화면과 같은 자리를 덮는다. */
function Preparing({ error, onClose }: { error: string | null; onClose: () => void }): ReactNode {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label="화면 편집 준비"
    >
      <div className="w-80 rounded-xl bg-white p-5 shadow-xl">
        {error ? <ErrorNote message={error} /> : <Loading />}
        <div className="mt-3 flex justify-end">
          <button type="button" className="btn" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
